import { SPECIALTIES, DEFAULT_SPECIALTY } from 'config/specialtyConfig';

/**
 * Returns the specialty-prefixed Firestore collection name.
 * For PMR (default): returns the original name (e.g. "questionBankSubjects")
 * For Pain: returns "painQuestionBankSubjects"
 * For Sports: returns "sportsQuestionBankSubjects"
 *
 * @param {string} baseCollectionName — the original collection name
 * @param {Function} getState — Redux getState
 */
export const getSpecialtyCollection = (baseCollectionName, getState) => {
  const activeSpecialty = getState?.()?.specialty?.activeSpecialty || DEFAULT_SPECIALTY;
  const config = SPECIALTIES[activeSpecialty];

  if (!config || !config.prefix) {
    return baseCollectionName;
  }

  // Capitalize first letter of the base name when prefixing:
  // "questionBankSubjects" → "painQuestionBankSubjects"
  return config.prefix + baseCollectionName.charAt(0).toUpperCase() + baseCollectionName.slice(1);
};

/**
 * Returns the counter document path for test progress numbering.
 * PMR: "counters/testProgress"
 * Pain: "counters/painTestProgress"
 * Sports: "counters/sportsTestProgress"
 */
export const getCounterDocPath = (getState) => {
  const activeSpecialty = getState?.()?.specialty?.activeSpecialty || DEFAULT_SPECIALTY;
  const config = SPECIALTIES[activeSpecialty];

  if (!config || !config.prefix) {
    return 'testProgress';
  }

  return config.prefix + 'TestProgress';
};

/**
 * Returns the specialty-prefixed collection name using a specialty ID directly.
 * Use this in components where getState is not available.
 *
 * @param {string} baseCollectionName — the original collection name
 * @param {string} specialtyId — the specialty ID (e.g. 'pmr', 'pain', 'sports')
 */
export const getCollectionNameForSpecialty = (baseCollectionName, specialtyId) => {
  const config = SPECIALTIES[specialtyId || DEFAULT_SPECIALTY];

  if (!config || !config.prefix) {
    return baseCollectionName;
  }

  return config.prefix + baseCollectionName.charAt(0).toUpperCase() + baseCollectionName.slice(1);
};

/**
 * Returns the current active specialty id from Redux state.
 */
export const getActiveSpecialty = (getState) => {
  return getState?.()?.specialty?.activeSpecialty || DEFAULT_SPECIALTY;
};

/**
 * Returns the specialty-prefixed Firebase Storage folder path.
 * PMR (default): "question-bank-images" (unchanged — preserves existing data)
 * Pain: "pain-question-bank-images"
 * Sports: "sports-question-bank-images"
 * Uses kebab-case prefix with dash separator (same convention as backend).
 */
export const getSpecialtyStorageFolder = (baseFolder, specialtyId) => {
  const config = SPECIALTIES[specialtyId || DEFAULT_SPECIALTY];

  if (!config || !config.prefix) {
    return baseFolder;
  }

  return config.prefix + '-' + baseFolder;
};

const STORAGE_BASE = 'https://storage.googleapis.com/pmr-bank.firebasestorage.app';

/**
 * Returns the full Firebase Storage URL for a file in a specialty-prefixed folder.
 * PMR: "https://storage.googleapis.com/pmr-bank.firebasestorage.app/img-tpc-courses/file.jpg"
 * Pain: "https://storage.googleapis.com/pmr-bank.firebasestorage.app/pain-img-tpc-courses/file.jpg"
 */
export const getStorageUrl = (baseFolder, filename, specialtyId) => {
  if (!filename) return null;
  const folder = getSpecialtyStorageFolder(baseFolder, specialtyId);
  return `${STORAGE_BASE}/${folder}/${filename}`;
};
